/*
 * HTTP common Base Instance
 */
import Vue from 'vue';
import axios from 'axios';
import jwt from 'jsonwebtoken';
import trackRequest from '@/plugins/MyTracker/directives/trackerRequest';
import store from '@/store';
import EventBus from '@/eventBus';

export const HTTP = axios.create({
  baseURL: process.env.SMS_CAMPAIGN_API_URL,
  timeout: 300000,
  headers: {},
  params: {},
});

let TRACK_EVENT_NAME = false;
let IS_IGNORE_TRACK_REQUEST = false;

/**
 *
 * @param {String} method HTTP method GET, POST, PUT, DELETE
 * @param {String} url
 * @param {Object} options
 * @param {String} objResponse
 */
/**
 * The Vuex store's in-memory `auth/access_token` getter resets to `null` on
 * every page load and only repopulates once the persistence plugin rehydrates
 * it from `localStorage` -- a race that, lost, sends `Bearer null` instead of
 * a real token. Read from the persisted snapshot first, matching what the
 * request interceptor below already trusts for expiry checks.
 * @returns {String}
 */
const getAccessToken = () => {
  try {
    const persisted = JSON.parse(localStorage.getItem('vuex'));
    if (persisted && persisted.auth && persisted.auth.access_token) {
      return persisted.auth.access_token;
    }
  } catch (e) {
    // fall through to the store getter below
  }
  return (store && store.getters['auth/access_token']) || '';
};

export const smsCampaignsApi = async (method, url, options = {}, objResponse) => {
  const token = getAccessToken();
  HTTP.defaults.headers.common.Authorization = `Bearer ${token}`;

  if (!url) throw new Error('Url param is required.');
  // combine default and passed in options if needed
  const updatedOptions = options.params
    ? {
      ...options,
      params: {
        ...HTTP.defaults.params,
        ...options.params,
      },
    }
    : {
      ...options,
      params: {
        ...HTTP.defaults.params,
      },
    };
  let response;
  switch (method) {
    case 'post':
    case 'patch': {
      // separate data from options
      // for some reason passing data as the 2nd parameter and in the options
      // is causing null data values to be converted into empty objects
      const data = updatedOptions.data || {};
      delete updatedOptions.data;
      response = await HTTP[method](url, data, updatedOptions);
      break;
    }
    default:
      response = await HTTP[method](url, updatedOptions);
      break;
  }
  let data = {};
  if (objResponse) data = response[objResponse];
  else data = response;
  return data;
};

const getRequestInfo = (data) => {
  let endpoint = (data.config.url).replace(`${data.config.baseURL}/`, '');
  const index = endpoint.indexOf('?');
  endpoint = index >= 0 ? endpoint.substring(0, index) : endpoint;
  const method = data.config.method;
  return [endpoint, method];
};

HTTP.interceptors.request.use(async (config) => {
  IS_IGNORE_TRACK_REQUEST = false;
  if (config.params) {
    if (config.params.trackrequest && config.params.tracker_event_name) {
      TRACK_EVENT_NAME = config.params.tracker_event_name;
    }
    IS_IGNORE_TRACK_REQUEST = !!config.params.isIgnoreTrackRequest;
    // eslint-disable-next-line no-param-reassign
    delete config.params.trackrequest;
    // eslint-disable-next-line no-param-reassign
    delete config.params.tracker_event_name;
    // eslint-disable-next-line no-param-reassign
    delete config.params.isIgnoreTrackRequest;
  } else if (config.data) {
    IS_IGNORE_TRACK_REQUEST = !!config.data.isIgnoreTrackRequest;
    // eslint-disable-next-line no-param-reassign
    delete config.data.isIgnoreTrackRequest;
  }

  if (process.env.NODE_ENV === 'testing') return config;

  const auth = JSON.parse(localStorage.getItem('vuex')).auth;
  if (auth.access_token) {
    const decoded = jwt.decode(auth.access_token) || {};
    const current = new Date();
    const expiration = new Date(decoded.exp * 1000);
    const isExpired = current.getTime() >= expiration.getTime();

    const decodedRefreshToken = jwt.decode(auth.refresh_token);
    const expRefreshToken = new Date(decodedRefreshToken.exp * 1000);
    const isRefreshTokenExpired = current.getTime() >= expRefreshToken.getTime();

    if (isRefreshTokenExpired) {
      Vue.config.errorHandler({ response: { status: 401 } });
      return {
        ...config,
      };
    }

    if (isExpired && config.url !== 'auth/token') {
      await store.dispatch('auth/refreshToken', auth);
      const auth2 = JSON.parse(localStorage.getItem('vuex')).auth;
      EventBus.$emit('setToken', auth2.access_token);
      // eslint-disable-next-line
      config.headers.Authorization = `Bearer ${auth2.access_token}`;
    }
  }

  return config;
}, (error) => Promise.reject(error));

HTTP.interceptors.response.use(async (response) => {
  if (response && response.config) {
    const [endpoint, method] = [...getRequestInfo(response)];
    trackRequest.emited({
      response,
      endpoint,
      method,
      event_name: TRACK_EVENT_NAME,
      isIgnoreTrackRequest: IS_IGNORE_TRACK_REQUEST,
    });
  }
  TRACK_EVENT_NAME = false;
  return response;
}, async (error) => {
  if (error && error.config) {
    const [endpoint, method] = [...getRequestInfo(error)];
    trackRequest.emited({
      response: error.response,
      endpoint,
      method,
      event_name: TRACK_EVENT_NAME,
      isIgnoreTrackRequest: IS_IGNORE_TRACK_REQUEST,
    });
  }
  TRACK_EVENT_NAME = false;
  return Promise.reject(error);
});
